<template>
  <v-main>
    <v-container fluid class="aeo-page-container">
      <v-row class="aeo-page-row">
        <v-col cols="12" class="aeo-page-col">
          <!-- Header Section -->
          <v-card class="mb-4">
            <v-card-text class="d-flex align-center justify-space-between flex-wrap pa-3 ga-3">
              <div class="d-flex align-center min-width-0 flex-grow-1">
                <v-menu
                  v-if="!loading && entities.length > 0"
                  v-model="entityMenuOpen"
                  :close-on-content-click="false"
                  location="bottom end"
                  :width="entityPickerMenuWidth"
                >
                  <template v-slot:activator="{ props: menuProps }">
                    <v-btn
                      ref="entityPickerActivatorRef"
                      v-bind="menuProps"
                      variant="flat"
                      class="entity-picker-trigger text-none font-weight-medium"
                      rounded="lg"
                      height="48"
                    >
                      <v-icon size="20" class="mr-2 flex-shrink-0 text-medium-emphasis">mdi-clipboard-text-search-outline</v-icon>
                      <span class="text-truncate text-body-1 text-high-emphasis">{{ headerSubtitle }}</span>
                      <v-icon size="22" class="ml-3 flex-shrink-0">mdi-chevron-down</v-icon>
                    </v-btn>
                  </template>
                  <v-card class="entity-picker-panel">
                    <v-list class="entity-picker-list py-2" density="comfortable" lines="two">
                      <v-list-item
                        v-for="item in ownEntities"
                        :key="item.id"
                        :active="item.id === selectedEntityId"
                        @click="selectEntityFromMenu(item.id)"
                      >
                        <template v-slot:prepend v-if="isAssessmentScanning(item)">
                          <v-progress-circular
                            indeterminate
                            color="primary"
                            size="24"
                            width="3"
                            class="mr-3"
                          ></v-progress-circular>
                        </template>
                        <v-list-item-title>{{ item.brand_name }} — {{ item.product_name }}</v-list-item-title>
                        <v-list-item-subtitle>
                          {{ item.region }}
                          <span
                            v-if="item.creator_team_id && item.creator_first_name && !isCurrentUserCreator(item)"
                            class="text-caption ml-1"
                            :class="{ 'team-creator-text': !isCurrentUserCreator(item) }"
                          >
                            •
                            {{ item.creator_first_name }}
                            <span v-if="item.creator_last_initial">
                              {{ item.creator_last_initial }}.
                            </span>
                          </span>
                          <span v-if="isAwaitingUserInput(item)" class="entity-pending-text"> • Pending</span>
                          <span v-else-if="isAssessmentScanning(item)" class="text-primary"> • Scanning…</span>
                        </v-list-item-subtitle>
                        <template v-slot:append>
                          <v-chip
                            :color="auditStatusChip(item.status).color"
                            size="x-small"
                            variant="tonal"
                            label
                            class="mr-2 text-none"
                          >
                            {{ auditStatusChip(item.status).text }}
                          </v-chip>
                          <v-btn
                            v-if="isCurrentUserCreator(item)"
                            icon
                            variant="text"
                            size="small"
                            color="grey"
                            :disabled="item.status === 'paused' || auditActionLoading"
                            :title="item.status === 'paused' ? 'Already paused' : 'Pause tracking'"
                            @click.stop="pauseEntityFromMenu(item)"
                          >
                            <v-icon size="small">mdi-pause-circle-outline</v-icon>
                          </v-btn>
                          <v-btn
                            v-if="isCurrentUserCreator(item) && isAdmin"
                            icon
                            variant="text"
                            size="small"
                            color="grey"
                            :disabled="auditActionLoading"
                            title="Delete"
                            @click.stop="confirmDeleteEntity(item)"
                          >
                            <v-icon size="small">mdi-delete</v-icon>
                          </v-btn>
                        </template>
                      </v-list-item>

                      <v-divider
                        v-if="teamEntities.length"
                        class="mt-2 mb-2"
                      ></v-divider>
                      <v-list-subheader
                        v-if="teamEntities.length"
                        class="team-subheader text-caption text-medium-emphasis"
                      >
                        Created by your team:
                      </v-list-subheader>
                      <v-list-item
                        v-for="item in teamEntities"
                        :key="item.id"
                        :active="item.id === selectedEntityId"
                        class="team-entity-item"
                        @click="selectEntityFromMenu(item.id)"
                      >
                        <template v-slot:prepend v-if="isAssessmentScanning(item)">
                          <v-progress-circular
                            indeterminate
                            color="primary"
                            size="24"
                            width="3"
                            class="mr-3"
                          ></v-progress-circular>
                        </template>
                        <v-list-item-title>{{ item.brand_name }} — {{ item.product_name }}</v-list-item-title>
                        <v-list-item-subtitle>
                          {{ item.region }}
                          <span
                            v-if="item.creator_team_id && item.creator_first_name"
                            class="text-caption ml-1 team-creator-text"
                          >
                            •
                            {{ item.creator_first_name }}
                            <span v-if="item.creator_last_initial">
                              {{ item.creator_last_initial }}.
                            </span>
                          </span>
                          <span v-if="isAwaitingUserInput(item)" class="entity-pending-text"> • Pending</span>
                          <span v-else-if="isAssessmentScanning(item)" class="text-primary"> • Scanning…</span>
                        </v-list-item-subtitle>
                        <template v-slot:append>
                          <v-chip
                            :color="auditStatusChip(item.status).color"
                            size="x-small"
                            variant="tonal"
                            label
                            class="text-none"
                          >
                            {{ auditStatusChip(item.status).text }}
                          </v-chip>
                        </template>
                      </v-list-item>
                    </v-list>
                    <v-divider class="opacity-50"></v-divider>
                    <div class="entity-picker-filter-wrap pa-4 pt-3">
                      <v-text-field
                        v-model="entityFilter"
                        class="entity-picker-filter"
                        density="compact"
                        variant="solo-filled"
                        placeholder="Filter AEO reviews"
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-magnify"
                        flat
                        rounded="lg"
                      ></v-text-field>
                    </div>
                  </v-card>
                </v-menu>
                <v-card-subtitle v-else class="pa-0 ma-0">
                  {{ headerSubtitle }}
                </v-card-subtitle>
                <v-chip
                  v-if="!loading && selectedEntityId"
                  :color="auditStatusChip(auditBannerStatus).color"
                  size="small"
                  variant="tonal"
                  label
                  class="ml-3 flex-shrink-0 text-none"
                >
                  {{ auditStatusChip(auditBannerStatus).text }}
                </v-chip>
              </div>
              <div
                v-if="!loading && showAnalyticsToolbar"
                class="d-flex align-stretch ga-3 ml-auto flex-shrink-0"
              >
                <v-select
                  v-model="selectedProvider"
                  :items="providerOptions"
                  item-title="label"
                  item-value="value"
                  density="comfortable"
                  variant="outlined"
                  hide-details
                  class="provider-select-toolbar"
                  @update:modelValue="onProviderChange"
                ></v-select>
                <button
                  type="button"
                  class="diagnosis-actions-badge"
                  :title="`${diagnosisActionsCount} diagnosis actions — open Action Center`"
                  aria-label="Open Action Center"
                  @click="openActionCenterTab"
                >
                  <v-icon size="16" class="diagnosis-actions-badge-icon" aria-hidden="true">mdi-lightning-bolt</v-icon>
                  <span class="diagnosis-actions-badge-count">{{ diagnosisActionsCount }}</span>
                  <span class="diagnosis-actions-badge-label">actions</span>
                </button>
              </div>
            </v-card-text>
          </v-card>

          <v-card v-if="loading" class="mb-6">
            <v-card-text class="text-center py-12">
              <v-progress-circular
                indeterminate
                color="primary"
                size="80"
                width="6"
                class="mb-4"
              ></v-progress-circular>
              <div class="text-h5 mb-2">Loading visibility analytics</div>
              <div class="text-body-1 text-medium-emphasis mb-4">
                Fetching your brand visibility data...
              </div>
              <div class="text-caption text-medium-emphasis">
                This may take a few seconds
              </div>
            </v-card-text>
          </v-card>

          <v-card v-else-if="!loading && entities.length === 0" class="mb-6">
            <v-card-text class="text-center py-12">
              <v-icon color="primary" size="80" class="mb-4">mdi-chart-line-variant</v-icon>
              <div class="text-h5 mb-2">No reviews yet</div>
              <div class="text-body-1 text-medium-emphasis mb-6">
                Start tracking your brand visibility across AI responses
              </div>
              <v-btn
                color="primary"
                size="large"
                @click="$router.push('/dashboard')"
              >
                <v-icon class="mr-2">mdi-plus</v-icon>
                Create your first review
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Idle lifecycle banner (warning / paused) shown above the tabs -->
          <AuditStatusBanner
            v-if="!loading && entities.length > 0 && selectedEntityId"
            :status="auditBannerStatus"
            :pause-reason="auditBannerPauseReason"
            :can-manage="isSelectedEntityCreator"
            :loading="auditActionLoading"
            @visit="onVisitKeepActive"
            @pause="onPauseAudit"
            @unpause="onUnpauseAudit"
          />

          <!-- Pipeline / ready Continue / scan animation -->
          <v-row v-if="!loading && entities.length > 0 && (isPipelineBlocking || analysisReady || showScanAnimation)" class="mb-6">
            <v-col cols="12">
              <AnalysisScanAnimation
                v-if="showScanAnimation"
                :brand-name="analytics?.review_entity?.brand_name || ''"
                :brand-url="analytics?.review_entity?.url || ''"
                :country="scanCountry"
                :region="analytics?.review_entity?.region || ''"
                :personas="personasFromAssessment"
                :queries="queriesFromAssessment"
                :products="scanProducts"
                :physician-pages="scanPhysicianPages"
                :page-source="scanPageSource"
                :location="scanLocation"
                :competitors="scanCompetitors"
                :competitor-names="scanCompetitorNames"
                :category-ownership="scanCategoryOwnership"
                :sentiment="scanSentiment"
                @complete="finishScanAnimation"
              />
              <AnalysisProgress
                v-else
                :tasks="analysisTasks"
                :progress="analysisProgress"
                :show-back-button="false"
                :analysis-ready="analysisReady"
                :review-entity-id="selectedEntityId"
                :current-processing-stage="currentProcessingStage"
                :personas="personasFromAssessment"
                :queries="queriesFromAssessment"
                :persona-profiles="personaProfiles"
                :competitors="competitorsFromAssessment"
                :needs-competitor-review="needsCompetitorReview"
                @continue="onAnalysisContinue"
                @view-details="viewCurrentEntity"
                @competitors-confirmed="handleCompetitorsConfirmed"
                @personas-confirmed="handlePersonasConfirmed"
                @queries-confirmed="handleQueriesConfirmed"
                @edit-personas="handleEditPersonas"
                @provider-questions-done="handleQueriesConfirmed"
                @edit-queries="handleEditPersonas"
              />
            </v-col>
          </v-row>

          <!-- Analytics dashboard (shown once grounded scoring and competitor comparison are complete) -->
          <template v-if="!loading && entities.length > 0 && canViewResults && !analysisReady && !showScanAnimation">
          
          <v-card v-if="isSelectedProviderLoading" class="mb-6">
            <v-card-text class="text-center py-12">
              <v-progress-circular
                indeterminate
                color="primary"
                size="80"
                width="6"
                class="mb-4"
              ></v-progress-circular>
              <div class="text-h5 mb-2">Loading {{ currentProviderLabel }} results</div>
              <div class="text-body-1 text-medium-emphasis mb-4">
                Query responses and scores for this provider are still being generated.
              </div>
              <div class="text-caption text-medium-emphasis">
                Gemini ⚡(Grounded) results are available — switch back to view them now.
              </div>
            </v-card-text>
          </v-card>

          <template v-else>
          <!-- Tabs Navigation -->
          <div class="tabs-container mb-8">
            <v-tabs v-model="currentTab" color="primary" align-tabs="center" height="80" grow>
              <v-tab value="queries">
                <v-icon class="mr-3">mdi-account-search</v-icon>
                <div class="d-flex flex-column align-start">
                  <span class="font-weight-medium">Personas &amp; Queries</span>
                  <span class="text-caption text-medium-emphasis">
                    {{ personaCount > 0 ? `${personaCount} personas` : 'View personas & queries' }}
                  </span>
                </div>
              </v-tab>
              <v-tab value="visibility">
                <v-icon class="mr-3">mdi-eye</v-icon>
                <div class="d-flex flex-column align-start">
                  <span class="font-weight-medium">Visibility</span>
                  <span class="text-caption text-medium-emphasis">
                    View visibility metrics
                  </span>
                </div>
              </v-tab>
              <v-tab value="consideration">
                <v-icon class="mr-3">mdi-bullhorn</v-icon>
                <div class="d-flex flex-column align-start">
                  <span class="font-weight-medium">Consideration</span>
                  <span class="text-caption text-medium-emphasis">
                    View consideration metrics
                  </span>
                </div>
              </v-tab>
              <v-tab v-if="providerTab === 'providers'" value="providers">
                <v-icon class="mr-3">mdi-account-group</v-icon>
                <div class="d-flex flex-column align-start">
                  <span class="font-weight-medium">Providers</span>
                  <span class="text-caption text-medium-emphasis">
                    Visibility of your providers
                  </span>
                </div>
              </v-tab>
              <v-tab v-else-if="providerTab === 'provider_review'" value="provider_review">
                <v-icon class="mr-3">mdi-account-group</v-icon>
                <div class="d-flex flex-column align-start">
                  <span class="font-weight-medium">Provider comparison</span>
                  <span class="text-caption text-medium-emphasis">
                    Compare against other providers
                  </span>
                </div>
              </v-tab>
              <v-tab value="sources">
                <v-icon class="mr-3">mdi-link-variant</v-icon>
                <div class="d-flex flex-column align-start">
                  <span class="font-weight-medium">Sources</span>
                  <span class="text-caption text-medium-emphasis">
                    See where AI pulls from
                  </span>
                </div>
              </v-tab>
              <v-tab value="action_center">
                <v-icon class="mr-3 aeo-action-tab-lightning">mdi-lightning-bolt-outline</v-icon>
                <div class="d-flex flex-column align-start">
                  <span class="font-weight-medium">Action center</span>
                  <span class="text-caption text-medium-emphasis">
                    Diagnosis reports and next actions
                  </span>
                </div>
              </v-tab>
            </v-tabs>
          </div>

          <v-window v-model="currentTab">

            <!-- Personas & Queries Tab -->
            <v-window-item value="queries">
              <div class="pa-6 pt-4 pb-2">
                <div class="d-flex align-center justify-space-between flex-wrap mb-1">
                  <div class="d-flex align-center mb-2 mb-sm-0">
                    <v-icon class="mr-3" color="primary">mdi-account-search</v-icon>
                    <span class="text-h5 font-weight-bold">Personas &amp; Queries</span>
                  </div>
                  <span class="text-body-2 text-medium-emphasis">
                    {{ personaCount > 0 ? `${personaCount} personas` : 'Generated personas will appear here' }}
                  </span>
                </div>
                <div class="text-caption text-medium-emphasis">
                  Generated Personas
                </div>
              </div>
              <HistoricalDateBanner
                :visible="showSnapshotBanner"
                :loading="loadingSnapshot"
                :load-target="snapshotLoadTarget"
                :selected-date="selectedHistoricalDate"
                :error="snapshotError"
                alert-class="mx-6 mb-4"
                @clear="onHistoricalDateCleared"
              />
              <v-alert v-if="!queries || queries.length === 0"
                type="info"
                variant="tonal"
                class="mb-4">
                No query data available for this assessment.
              </v-alert>
              <v-alert v-else-if="!personaProfiles || personaProfiles.length === 0"
                type="warning"
                variant="tonal"
                class="mb-4">
                Persona data is required to analyze queries by persona.
              </v-alert>
              <template v-else>
                <div
                  v-for="(personaGroup, groupIndex) in queriesByPersona"
                  :key="groupIndex"
                  class="mb-8"
                >
                  <v-card variant="outlined" class="persona-card mb-4">
                    <v-card-text class="pa-5">
                      <div class="d-flex flex-column mb-4">
                        <div class="mb-3">
                          <div class="d-flex align-center mb-2">
                            <v-icon class="mr-3 persona-query-icon" color="primary">mdi-account</v-icon>
                            <span class="persona-query-title">
                              {{ personaGroup.persona.name || `Persona ${groupIndex + 1}` }}
                            </span>
                          </div>
                          <div
                            v-if="personaGroup.persona.motivation"
                            class="persona-query-motivation"
                          >
                            {{ personaGroup.persona.motivation }}
                          </div>
                        </div>
                      </div>

                      <v-row class="mb-4">
                        <v-col cols="12" md="6" class="mb-3 mb-md-0">
                          <div class="text-caption text-medium-emphasis mb-1">
                            Demographics
                          </div>
                          <div
                            v-if="personaGroup.persona.demographic"
                            class="persona-query-details"
                          >
                            <div
                              v-if="personaGroup.persona.demographic.age_range"
                              class="persona-detail-row"
                            >
                              <v-icon size="small" class="persona-detail-icon">
                                mdi-calendar-range
                              </v-icon>
                              <div>
                                <div class="persona-detail-label">Age Range</div>
                                <div class="persona-detail-value">
                                  {{ personaGroup.persona.demographic.age_range }}
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="personaGroup.persona.demographic.location"
                              class="persona-detail-row"
                            >
                              <v-icon size="small" class="persona-detail-icon">
                                mdi-map-marker
                              </v-icon>
                              <div>
                                <div class="persona-detail-label">Location</div>
                                <div class="persona-detail-value">
                                  {{ personaGroup.persona.demographic.location }}
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="personaGroup.persona.demographic.income_bracket"
                              class="persona-detail-row"
                            >
                              <v-icon size="small" class="persona-detail-icon">
                                mdi-cash
                              </v-icon>
                              <div>
                                <div class="persona-detail-label">Income Bracket</div>
                                <div class="persona-detail-value">
                                  {{ personaGroup.persona.demographic.income_bracket }}
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="personaGroup.persona.demographic.gender_focus"
                              class="persona-detail-row"
                            >
                              <v-icon size="small" class="persona-detail-icon">
                                mdi-gender-male-female
                              </v-icon>
                              <div>
                                <div class="persona-detail-label">Gender Focus</div>
                                <div class="persona-detail-value">
                                  {{ personaGroup.persona.demographic.gender_focus }}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            v-else
                            class="text-caption text-medium-emphasis"
                          >
                            No demographic details provided.
                          </div>
                        </v-col>

                        <v-col cols="12" md="6">
                          <div class="text-caption text-medium-emphasis mb-1 d-flex align-center">
                            <v-icon size="small" class="mr-1 text-medium-emphasis">
                              mdi-clock-outline
                            </v-icon>
                            <span>Purchase Motivation</span>
                          </div>
                          <div class="text-body-2">
                            {{ personaGroup.persona.motivation || 'No purchase motivation provided.' }}
                          </div>
                        </v-col>
                      </v-row>

                      <v-row class="mb-4">
                        <v-col cols="12">
                          <div class="text-caption text-medium-emphasis mb-1 d-flex align-center">
                            <v-icon size="small" class="mr-1" color="primary">
                              mdi-clipboard-text-outline
                            </v-icon>
                            <span>Product Focus</span>
                          </div>
                          <div class="text-body-2">
                            {{ personaGroup.persona.product_focus || 'No product focus provided.' }}
                          </div>
                        </v-col>
                      </v-row>

                      <div class="mt-2">
                        <div class="text-subtitle-2 font-weight-medium mb-2 d-flex align-center">
                          <v-icon size="small" class="mr-2" color="primary">mdi-magnify</v-icon>
                          Related Queries
                        </div>
                        <v-expansion-panels variant="accordion" class="persona-query-panel">
                          <v-expansion-panel
                            v-for="query in personaGroup.queries"
                            :key="query.id"
                          >
                            <v-expansion-panel-title>
                              <div class="d-flex align-center justify-space-between w-100 pr-4">
                                <div class="text-subtitle-1 font-weight-medium">
                                  {{ query.query_text }}
                                </div>
                              </div>
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                              <v-divider class="mb-4"></v-divider>

                              <div class="mb-4">
                                <div class="text-subtitle-2 font-weight-bold mb-2">
                                  AI Response
                                </div>
                                <div
                                  class="text-body-2 pa-3 bg-grey-lighten-4 rounded formatted-response"
                                  v-html="query.response || 'No response available'"
                                ></div>
                              </div>
                            </v-expansion-panel-text>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>

                <!-- Grounded Response Analysis Section (only show for grounded responses) -->
                <v-card v-if="groundedResponsesWithCitations.length > 0" variant="outlined" class="mt-6">
                  <v-card-title class="d-flex align-center pa-6">
                    <v-icon class="mr-3" color="primary">mdi-text-box-check</v-icon>
                    Grounded Response Analysis
                    <v-chip class="ml-3" size="small" color="primary" variant="tonal">
                      {{ totalGroundedSegments }} verified claims
                    </v-chip>
                  </v-card-title>
                  <v-card-text class="pt-0">
                    <div class="text-body-2 text-medium-emphasis mb-4">
                      Each AI claim below is backed by web sources. Click sources to verify accuracy.
                    </div>

                    <!-- Per-query grounded segments -->
                    <v-expansion-panels variant="accordion" class="grounded-panels">
                      <v-expansion-panel
                        v-for="(queryData, qIdx) in groundedResponsesWithCitations"
                        :key="qIdx"
                      >
                        <v-expansion-panel-title>
                          <div class="d-flex align-start flex-wrap">
                            <v-icon size="small" class="mr-2 mt-1" color="primary">mdi-comment-question</v-icon>
                            <span class="font-weight-medium grounded-query-text">{{ queryData.query_text }}</span>
                            <v-chip size="x-small" class="ml-3 mt-1" color="success" variant="tonal">
                              {{ queryData.segments.length }} grounded claims
                            </v-chip>
                          </div>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <div 
                            v-for="(segment, sIdx) in queryData.segments" 
                            :key="sIdx"
                            class="grounded-segment mb-4"
                          >
                            <!-- The grounded text -->
                            <div class="segment-text pa-3 rounded-t">
                              <v-icon size="small" class="mr-2 segment-quote-icon">mdi-format-quote-open</v-icon>
                              <span class="segment-content">{{ segment.text }}</span>
                            </div>
                            
                            <!-- Supporting sources -->
                            <div class="segment-sources pa-3 rounded-b">
                              <div class="text-caption text-medium-emphasis mb-2 d-flex align-center">
                                <v-icon size="x-small" class="mr-1">mdi-link-variant</v-icon>
                                Backed by {{ segment.sources.length }} {{ segment.sources.length === 1 ? 'source' : 'sources' }}:
                              </div>
                              <div class="sources-chips">
                                <a
                                  v-for="(source, srcIdx) in segment.sources"
                                  :key="srcIdx"
                                  :href="source.final_url || source.uri"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  class="source-chip"
                                >
                                  <v-icon size="x-small" class="source-chip-icon">mdi-open-in-new</v-icon>
                                  <span class="source-chip-label">{{ source.title || extractDomain(source.final_url || source.uri) }}</span>
                                  <v-tooltip
                                    v-if="source.validated"
                                    location="top"
                                  >
                                    <template v-slot:activator="{ props }">
                                      <v-icon
                                        v-bind="props"
                                        size="x-small"
                                        color="success"
                                        class="source-chip-check"
                                      >
                                        mdi-check-decagram
                                      </v-icon>
                                    </template>
                                    <span>Verified URL</span>
                                  </v-tooltip>
                                </a>
                              </div>
                            </div>
                          </div>
                        </v-expansion-panel-text>
                      </v-expansion-panel>
                    </v-expansion-panels>

                    <!-- Search queries used for grounding -->
                    <div v-if="groundingSearchQueries.length > 0" class="mt-6">
                      <div class="text-caption text-medium-emphasis mb-2 d-flex align-center">
                        <v-icon size="x-small" class="mr-1">mdi-magnify</v-icon>
                        Search queries used
                      </div>
                      <div class="search-queries-container">
                        <v-chip
                          v-for="(query, idx) in groundingSearchQueries.slice(0, 6)"
                          :key="idx"
                          size="x-small"
                          variant="outlined"
                          class="ma-1"
                        >
                          {{ query }}
                        </v-chip>
                        <v-chip
                          v-if="groundingSearchQueries.length > 6"
                          size="x-small"
                          variant="text"
                          class="ma-1"
                        >
                          +{{ groundingSearchQueries.length - 6 }} more
                        </v-chip>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </template>
              <ProviderQuestionsSection
                v-if="providersPageEnabled"
                :reviewEntityId="selectedEntityId"
                :providers="providerList"
                @go-to-tab="onGoToTab"
              />
            </v-window-item>

            <!-- Visibility Tab -->
            <v-window-item value="visibility">
              <VisibilityTab 
                :queries="queries"
                :personaProfiles="personaProfiles"
                :reviewEntityId="selectedEntityId"
                :selectedProvider="selectedProvider"
                :selectedHistoricalDate="selectedHistoricalDate"
                :loadingSnapshot="loadingSnapshot"
                :snapshotLoadTarget="snapshotLoadTarget"
                :showSnapshotBanner="showSnapshotBanner"
                :snapshotError="snapshotError"
                :primaryBrandName="analytics?.review_entity?.brand_name"
                :brandMatchAliases="analytics?.review_entity?.brand_match_aliases || []"
                @brand-match-aliases-updated="onBrandMatchAliasesUpdated"
                @visibility-rescored="onVisibilityRescored"
                @historical-date-selected="onHistoricalDateSelected"
                @historical-date-cleared="onHistoricalDateCleared"
              />
            </v-window-item>

            <!-- Consideration Tab -->
            <v-window-item value="consideration">
              <ConsiderationTab 
                :queries="queries"
                :personaProfiles="personaProfiles"
                :reviewEntityId="selectedEntityId"
                :selectedProvider="selectedProvider"
                :selectedHistoricalDate="selectedHistoricalDate"
                :loadingSnapshot="loadingSnapshot"
                :snapshotLoadTarget="snapshotLoadTarget"
                :showSnapshotBanner="showSnapshotBanner"
                :snapshotError="snapshotError"
                :assessmentId="analytics?.latest_assessment?.id"
                :primaryBrandName="analytics?.review_entity?.brand_name"
                @historical-date-selected="onHistoricalDateSelected"
                @historical-date-cleared="onHistoricalDateCleared"
              />
            </v-window-item>
            <v-window-item v-if="providerTab === 'providers'" value="providers">
              <ProvidersTab
                :review-entity-id="selectedEntityId"
                :selected-provider="selectedProvider"
                :hospital="analytics?.review_entity?.brand_name || ''"
                :service-line="analytics?.review_entity?.product_name || ''"
                :region="analytics?.review_entity?.region || ''"
                @providers-changed="onProvidersChanged"
                @providers-loaded="providerList = $event"
                @go-to-tab="onGoToTab"
              />
            </v-window-item>
            <!-- Old Provider comparison tab: teams without the providers-page flag -->
            <v-window-item v-else-if="providerTab === 'provider_review'" value="provider_review">
              <ProviderReviewTab
                :assessmentId="analytics?.latest_assessment?.id"
                :selectedProvider="selectedProvider"
                :canCreateProvider="canCreateProviderComparison"
                @provider-names-changed="onProviderNamesChanged"
                @provider-comparison-pending="providerComparisonPending = $event"
              />
            </v-window-item>
            <v-window-item value="sources">
              <SourcesTab
                :queries="queries"
                :reviewEntityUrl="analytics?.review_entity?.url || ''"
                :brandMatchAliases="analytics?.review_entity?.brand_match_aliases || []"
                :primaryBrandName="analytics?.review_entity?.brand_name || ''"
                :selectedProvider="selectedProvider"
                :providerLabel="currentProviderLabel"
                :scannedAt="analytics?.latest_assessment?.created_at"
                :selectedHistoricalDate="selectedHistoricalDate"
                :loadingSnapshot="loadingSnapshot"
                :snapshotLoadTarget="snapshotLoadTarget"
                :showSnapshotBanner="showSnapshotBanner"
                :snapshotError="snapshotError"
                @switch-provider="onSwitchProvider"
                @historical-date-cleared="onHistoricalDateCleared"
              />
            </v-window-item>
            <v-window-item value="action_center" eager>
              <ActionCenterTab
                :assessmentId="analytics?.latest_assessment?.id"
                :review-entity-id="selectedEntityId"
                :review-entity="analytics?.review_entity"
                :selectedProvider="selectedProvider"
                :queries="queries"
                :diagnosis-findings="analytics?.diagnosis_findings || []"
                :diagnosis-preview="analytics?.diagnosis_preview"
                :diagnosis-setup="providersPageEnabled ? analytics?.diagnosis_setup : actionCenterDiagnosisSetup"
                :providers="providerList"
                :provider-comparison-pending="providerComparisonPending"
                @go-to-tab="onGoToTab"
                @diagnosis-setup-updated="onDiagnosisSetupUpdated"
                @diagnosis-started="onDiagnosisStarted"
              />
            </v-window-item>
          </v-window>
          </template>
          </template>
        </v-col>
      </v-row>

      <!-- Admin-only: replay the post-analysis scan animation -->
      <v-row v-if="isAdmin && selectedEntityId && !showScanAnimation && !analysisReady" justify="center" class="mt-4">
        <v-col cols="auto">
          <v-btn
            variant="text"
            size="small"
            color="medium-emphasis"
            @click="resetScanAnimationForAdmin"
          >
            Watch Animation Again
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
  
  <!-- Delete Confirmation Dialog -->
  <v-dialog v-model="deleteDialog" max-width="400">
    <v-card>
      <v-card-title class="text-h6">Delete Review Entity?</v-card-title>
      <v-card-text>
        <p class="mb-2">Are you sure you want to delete <strong>{{ entityToDelete?.brand_name }} — {{ entityToDelete?.product_name }}</strong>?</p>
        <p class="text-medium-emphasis text-body-2">This will permanently delete all associated assessments, personas, queries, and scores.</p>
      </v-card-text>
      <v-card-actions>
        <v-btn 
          color="primary" 
          variant="outlined" 
          :loading="duplicateLoading" 
          @click="duplicateEntity"
          class="mr-2"
        >
          Re-run
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="deleteDialog = false">Cancel</v-btn>
        <v-btn color="error" variant="flat" :loading="deleteLoading" @click="deleteEntity">Delete</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

</template>

<script>
import axios from '@/axios.js'
import { mapGetters, mapState } from 'vuex'
import AnalysisProgress from '@/components/AnalysisProgress.vue'
import AnalysisScanAnimation from '@/components/AnalysisScanAnimation.vue'
import VisibilityTab from '@/components/VisibilityTab.vue'
import ConsiderationTab from '@/components/ConsiderationTab.vue'
import { listProviders } from '@/components/providers/providersApi'
import ProvidersTab from '@/components/providers/ProvidersTab.vue'
import ProviderQuestionsSection from '@/components/providers/ProviderQuestionsSection.vue'
import ProviderReviewTab from '@/components/ProviderReviewTab.vue'
import ActionCenterTab from '@/components/ActionCenterTab.vue'
import SourcesTab from '@/components/SourcesTab.vue'
import HistoricalDateBanner from '@/components/HistoricalDateBanner.vue'
import AuditStatusBanner from '@/components/AuditStatusBanner.vue'
import {
  canViewAnalyticsResults,
  isPipelineBlocking as assessmentIsPipelineBlocking,
  isProviderReady,
  assessmentNeedsPolling,
  needsCompetitorConfirmation,
  analysisStageToTaskKey
} from '@/utils/assessmentReadiness'
import { isWithinHistoricalWindow, HISTORICAL_WINDOW_DAYS } from '@/utils/historicalData'
import { computeCategoryOwnershipScore, computeSentimentScore } from '@/utils/scanMetrics'
import { shouldPlayScanAnimation, markScanAnimationPlayed, clearScanAnimationPlayed } from '@/utils/scanAnimationPlayed'

const BASE_URL = process.env.VUE_APP_API_BASE_URL
// Action Center refreshes slowly while diagnosis runs (minutes) or while a scan
// that already shows results is still being marked completed. Backs off, pauses
// in hidden tabs, and gives up after an hour; a reload starts it again.
const SLOW_REFRESH_DELAYS_MS = [15000, 30000, 60000]
const SLOW_REFRESH_MAX_MS = 60 * 60 * 1000
// Processing stages where the scan waits for the user instead of running jobs.
const AWAITING_USER_STAGES = ['confirm_competitors', 'confirm_personas', 'confirm_queries', 'confirm_provider_queries']

export default {
  name: 'AeoAnalytics',
  components: {
    AnalysisProgress,
    AnalysisScanAnimation,
    VisibilityTab,
    ConsiderationTab,
    ProvidersTab,
    ProviderQuestionsSection,
    ProviderReviewTab,
    ActionCenterTab,
    SourcesTab,
    HistoricalDateBanner,
    AuditStatusBanner
  },
  data() {
    return {
      loading: true,
      selectedEntityId: null,
      // The audit's doctors, with each one's setup_stage. Feeds the questions section and the Action Center to-do.
      providerList: [],
      // Old Provider comparison tab (no providers-page flag).
      providerCountsByAssessment: {},
      providerComparisonPending: false,
      analytics: null,
      showScanAnimation: false,
      /** Completed pipeline on this page — show Continue before scan/results. */
      analysisReady: false,
      scanEnrichment: null,
      entityMenuOpen: false,
      entityPickerMenuWidth: 320,
      entityFilter: '',
      pollingInterval: null,
      pollingInFlight: false,
      slowRefreshTimer: null,
      slowRefreshInFlight: false,
      slowRefreshStartedAt: null,
      slowRefreshTick: 0,
      currentTab: 'queries', // Default to Query Analysis tab
      selectedProvider: 'google_grounded',
      providerOptions: [
        { label: 'Gemini ⚡(Grounded)', value: 'google_grounded' },
        { label: 'ChatGPT', value: 'openai' },
        { label: 'Gemini', value: 'google' }
      ],
      deleteDialog: false,
      deleteLoading: false,
      entityToDelete: null,
      duplicateLoading: false,
      selectedHistoricalDate: null,
      loadingSnapshot: false,
      snapshotLoadTarget: null,
      analyticsFetchId: 0,
      snapshotError: null,
      visitRecordedForEntityId: null,
      auditActionLoading: false
    }
  },
  computed: {
    ...mapState(['auth']),
    ...mapGetters(['getReviewEntities', 'isAdmin', 'getUserID', 'providersPageEnabled']),
    // Which provider tab to show: the new Providers tab behind the providers-page flag, else
    // the old Provider comparison tab. Neither until the user (and so the flag) has loaded.
    providerTab() {
      if (!this.getUserID) return null
      return this.providersPageEnabled ? 'providers' : 'provider_review'
    },
    showSnapshotBanner() {
      return this.loadingSnapshot || Boolean(this.selectedHistoricalDate)
    },
    entities() {
      // Vuex list summaries can disagree with GET /review_entities/:id (e.g. race or id
      // string/number mismatch on selection). Overlay the detail payload onto that row so the
      // picker matches what the page already rendered.
      const rows = this.getReviewEntities || []
      const liveEntityId = this.analytics?.review_entity?.id
      const live = this.analytics?.latest_assessment

      return rows.map(e => {
        const base = {
          ...e,
          _title: `${e.brand_name}${e.product_name ? ' — ' + e.product_name : ''}`
        }
        const overlay =
          live &&
          liveEntityId != null &&
          Number(liveEntityId) === Number(e.id)
        if (!overlay) return base

        return {
          ...base,
          latest_assessment: {
            ...(e.latest_assessment || {}),
            id: live.id,
            status: live.status,
            processing_stage: live.processing_stage,
            overall_aeo_score: live.overall_aeo_score ?? e.latest_assessment?.overall_aeo_score,
            llm_provider: live.llm_provider ?? e.latest_assessment?.llm_provider,
            provider_readiness: live.provider_readiness ?? e.latest_assessment?.provider_readiness,
            product_comparison_ready: live.product_comparison_ready ?? e.latest_assessment?.product_comparison_ready,
            analytics_ready: live.analytics_ready ?? e.latest_assessment?.analytics_ready
          }
        }
      })
    },
    selectedEntity() {
      if (this.selectedEntityId == null) return null
      return this.entities.find(e => Number(e.id) === Number(this.selectedEntityId)) || null
    },
    // Prefer the freshly-loaded detail payload for the selected entity, falling
    // back to the (possibly staler) list summary.
    auditBannerStatus() {
      const detail = this.analytics?.review_entity
      if (detail && Number(detail.id) === Number(this.selectedEntityId) && detail.status) {
        return detail.status
      }
      return this.selectedEntity?.status || 'active'
    },
    auditBannerPauseReason() {
      const detail = this.analytics?.review_entity
      if (detail && Number(detail.id) === Number(this.selectedEntityId)) {
        return detail.pause_reason || null
      }
      return this.selectedEntity?.pause_reason || null
    },
    // Only the creator can pause/unpause; drives the banner's management buttons.
    isSelectedEntityCreator() {
      return this.isCurrentUserCreator(this.selectedEntity)
    },
    currentUserId() {
      return this.getUserID
    },
    headerSubtitle() {
      if (this.entities.length === 0) return ''
      const ent = this.entities.find(e => e.id === this.selectedEntityId)
      if (!ent) return '—'
      return `${ent.brand_name} ${ent.product_name ? '– ' + ent.product_name : ''}`
    },
    filteredEntities() {
      const nonArchived = (this.entities || []).filter(
        (e) => !e.archived_at && e.status !== 'archived'
      )
      const q = (this.entityFilter || '').toLowerCase().trim()
      if (!q) return nonArchived
      return nonArchived.filter(e => {
        const brand = (e.brand_name || '').toLowerCase()
        const prod = (e.product_name || '').toLowerCase()
        return brand.includes(q) || prod.includes(q)
      })
    },
    ownEntities() {
      // Default any entities without creator_id to "own" for backward compatibility
      return this.filteredEntities.filter(e => !e.creator_id || e.creator_id === this.currentUserId)
    },
    teamEntities() {
      return this.filteredEntities.filter(e => e.creator_id && e.creator_id !== this.currentUserId)
    },
    canCreateProviderComparison() {
      const ent = this.entities.find(e => e.id === this.selectedEntityId)
      if (!ent) return false
      // Backward compatibility: if creator_id is missing, preserve existing behavior.
      if (!ent.creator_id) return true
      return ent.creator_id === this.currentUserId
    },
    personaProfiles() {
      const profiles = this.analytics?.latest_assessment?.personas || []
      // Ensure we return an array
      return Array.isArray(profiles) ? profiles : []
    },
    personaCount() {
      return this.personaProfiles.length
    },
    queries() {
      return this.analytics?.queries || []
    },
    diagnosisRunning() {
      return this.queries.some((query) => this.isDiagnosisReportPending(query?.diagnosis_report?.status))
    },
    // Old Provider comparison tab: the server counts a provider only once its first comparison
    // job saves a row; the tab lists it straight away. Show the higher of the two.
    actionCenterDiagnosisSetup() {
      const setup = this.analytics?.diagnosis_setup
      if (!setup) return null
      const listed = this.providerCountsByAssessment[setup.assessment_id] || 0
      return { ...setup, provider_count: Math.max(setup.provider_count || 0, listed) }
    },
    scanFinishing() {
      const assessment = this.analytics?.latest_assessment
      return assessment?.status === 'pending' && canViewAnalyticsResults(assessment)
    },
    // providers_pending comes from the server, so refresh until it clears.
    needsSlowRefresh() {
      return this.diagnosisRunning || this.scanFinishing ||
        this.analytics?.diagnosis_setup?.providers_pending === true
    },
    queryCount() {
      return this.queries.length
    },
    queriesByPersona() {
      const personas = this.personaProfiles
      const allQueries = this.queries

      // Group queries by persona (3 queries per persona)
      return personas.map((persona, index) => {
        const startIndex = index * 3
        const endIndex = startIndex + 3
        const personaQueries = allQueries.slice(startIndex, endIndex)

        return {
          persona: persona,
          queries: personaQueries,
          personaIndex: index
        }
      }).filter(group => group.queries.length > 0)
    },
    isCurrentAssessmentProcessing() {
      const latest = this.analytics?.latest_assessment
      if (!latest) return false
      const status = latest.status
      return status === 'pending' || status === 'processing'
    },
    latestAssessment() {
      return this.analytics?.latest_assessment ||
        this.entities.find(e => e.id === this.selectedEntityId)?.latest_assessment ||
        null
    },
    isPipelineBlocking() {
      return assessmentIsPipelineBlocking(this.latestAssessment)
    },
    needsCompetitorReview() {
      return needsCompetitorConfirmation(this.latestAssessment)
    },
    canViewResults() {
      return canViewAnalyticsResults(this.latestAssessment)
    },
    selectedEntityCreatedAt() {
      return this.analytics?.review_entity?.created_at
        || this.entities.find((e) => e.id === this.selectedEntityId)?.created_at
        || null
    },
    showAnalyticsToolbar() {
      return this.canViewResults && !this.analysisReady && !this.showScanAnimation
    },
    scanCountry() {
      const region = this.analytics?.review_entity?.region || ''
      if (/canada/i.test(region)) return 'Canada'
      if (/united states|\busa\b/i.test(region)) return 'United States'
      const first = region.split(',')[0]?.trim()
      return first || 'United States'
    },
    scanProducts() {
      const enriched = this.scanEnrichment?.service_lines
      if (Array.isArray(enriched) && enriched.length) {
        return enriched.slice(0, 4).map((line) => ({
          name: line.name,
          description: line.description || '',
          selected: !!line.selected,
          photo: line.photo || null
        }))
      }
      const name = this.analytics?.review_entity?.product_name
      if (!name) return []
      return [{
        name,
        description: this.analytics?.review_entity?.product_description || '',
        selected: true
      }]
    },
    scanPhysicianPages() {
      return this.scanEnrichment?.physician_pages || null
    },
    scanPageSource() {
      return this.scanEnrichment?.page_source || null
    },
    scanLocation() {
      return this.scanEnrichment?.location || null
    },
    scanCompetitors() {
      const comps = this.scanEnrichment?.competitors
      return Array.isArray(comps) ? comps.filter((c) => c?.name && c.lat != null && c.lng != null) : []
    },
    scanCompetitorNames() {
      const products = this.competitorsFromAssessment?.products
      if (!Array.isArray(products)) return []
      return products
        .filter((p) => p?.name && !p.is_primary)
        .map((p) => p.name)
        .slice(0, 6)
    },
    scanCategoryOwnership() {
      return computeCategoryOwnershipScore(this.queriesFromAssessment || [])
    },
    scanSentiment() {
      return computeSentimentScore(this.queriesFromAssessment || [])
    },
    providerReadiness() {
      return this.latestAssessment?.provider_readiness || {}
    },
    isSelectedProviderLoading() {
      return !isProviderReady(this.latestAssessment, this.selectedProvider)
    },
    assessmentNeedsPolling() {
      return assessmentNeedsPolling(this.latestAssessment)
    },
    hasAnyProcessingEntities() {
      return this.assessmentNeedsPolling
    },
    processingStageLabel() {
      const ent = this.entities.find(e => e.id === this.selectedEntityId)
      if (!ent || !ent.latest_assessment) return 'Initializing...'
      const stage = ent.latest_assessment.processing_stage
      
      switch(stage) {
        case 'persona_creation':
          return 'Creating User Persona'
        case 'queries_generation':
          return 'Generating Search Queries'
        case 'query_scoring':
          return 'Scoring Query Results'
        case 'confirm_competitors':
          return 'Review Competitors'
        case 'confirm_provider_queries':
          return 'Review provider questions'
        case 'competitor_comparison':
          return 'Analyzing Competitors'
        default:
          return 'In Progress...'
      }
    },
    currentProcessingStage() {
      // Use analytics first because polling refreshes this object every cycle.
      // Falling back to entities keeps initial render behavior unchanged.
      return this.analytics?.latest_assessment?.processing_stage ||
             this.entities.find(e => e.id === this.selectedEntityId)?.latest_assessment?.processing_stage ||
             null
    },
    personasFromAssessment() {
      return this.analytics?.latest_assessment?.personas || []
    },
    queriesFromAssessment() {
      return this.queries || []
    },
    competitorsFromAssessment() {
      return this.analytics?.latest_assessment?.comparison?.competitor_data || null
    },
    analysisTasks() {
      const assessment = this.analytics?.latest_assessment ||
                         this.entities.find(e => e.id === this.selectedEntityId)?.latest_assessment
      if (!assessment) {
        return [
          {
            key: 'persona_creation',
            label: 'Creating User Personas',
            status: 'pending',
            message: 'Generating brand-specific user persona...'
          },
          {
            key: 'queries_generation',
            label: 'Generating Search Queries',
            status: 'pending',
            message: 'Creating relevant search queries...'
          },
          {
            key: 'query_execution',
            label: 'Running queries as personas',
            status: 'pending',
            message: 'Executing queries and generating responses...'
          },
          {
            key: 'query_scoring',
            label: 'Scoring Query Results',
            status: 'pending',
            message: 'Analyzing and scoring AI responses...'
          }
        ]
      }
      
      const currentStage = assessment.processing_stage
      const status = assessment.status
      
      const tasks = [
        {
          key: 'persona_creation',
          label: 'Creating User Personas',
          status: 'pending',
          message: 'Creating customer personas...'
        },
        {
          key: 'queries_generation',
          label: 'Generating Search Queries',
          status: 'pending',
          message: 'Generating search queries as customer personas...'
        },
        {
          key: 'query_execution',
          label: 'Running queries as personas',
          status: 'pending',
          message: 'Running queries as personas and generating responses...'
        },
        {
          key: 'query_scoring',
          label: 'Scoring Query Results',
          status: 'pending',
          message: 'Scoring brand performance...'
        },
        {
          key: 'review_competitors',
          label: 'Review Competitors',
          status: 'pending',
          message: 'Review competitors from query results...'
        },
        {
          key: 'competitor_comparison',
          label: 'Comparing Products',
          status: 'pending',
          message: 'Comparing products with competitors...'
        }
      ]
      
      if (status === 'failed') {
        const taskKey = analysisStageToTaskKey(currentStage, assessment)
        const stageIndex = tasks.findIndex(t => t.key === taskKey)
        tasks.forEach((task, index) => {
          if (index < stageIndex) {
            task.status = 'completed'
          } else if (index === stageIndex) {
            task.status = 'failed'
            task.message = 'Processing failed'
          }
        })
        return tasks
      }
      
      if (!currentStage) {
        return tasks
      }
      
      const taskKey = analysisStageToTaskKey(currentStage, assessment)
      const stageIndex = taskKey ? tasks.findIndex(t => t.key === taskKey) : -1
      
      if (stageIndex >= 0) {
        tasks.forEach((task, index) => {
          if (index < stageIndex) {
            task.status = 'completed'
          } else if (index === stageIndex) {
            if (task.key === 'review_competitors' || currentStage.startsWith('confirm_')) {
              task.status = 'action-required'
              if (task.key === 'review_competitors' || currentStage === 'confirm_competitors') {
                task.message = 'Review the competitors most mentioned in your query responses below'
              } else if (currentStage === 'confirm_personas') {
                task.message = 'Please review and confirm the generated personas below'
              } else if (currentStage === 'confirm_queries') {
                task.message = 'Please review and confirm the generated queries below'
              } else if (currentStage === 'confirm_provider_queries') {
                task.message = 'Please review the provider questions below'
              }
            } else {
              task.status = 'in-progress'
              const messages = {
                'persona_creation': 'Creating customer personas...',
                'queries_generation': 'Generating search queries as customer personas...',
                'query_execution': 'Running queries as personas and generating responses...',
                'query_scoring': 'Scoring brand performance...',
                'competitor_comparison': 'Comparing products with competitors...'
              }
              task.message = messages[task.key] || task.message
            }
          }
        })
      }
      
      return tasks
    },
    analysisProgress() {
      const tasks = this.analysisTasks
      const completedCount = tasks.filter(t => t.status === 'completed').length
      return (completedCount / tasks.length) * 100
    },
    currentProvider() {
      // Get provider from latest assessment, default to 'openai'
      const provider = this.analytics?.latest_assessment?.llm_provider
      return provider || ''
    },
    currentProviderLabel() {
      // Format the provider name for display
      const provider = this.selectedProvider || this.currentProvider
      const providerMap = {
        'openai': 'ChatGPT (OpenAI)',
        'google': 'Gemini (Google)',
        'google_grounded': 'Gemini ⚡(Grounded)'
      }
      return providerMap[provider] || provider
    },
    // Extract all citations from queries
    allCitations() {
      if (!this.queries || this.queries.length === 0) {
        return []
      }
      
      const citations = []
      this.queries.forEach(query => {
        if (query.citations && Array.isArray(query.citations)) {
          citations.push(...query.citations)
        }
      })
      return citations
    },
    // Extract search queries used for grounding
    groundingSearchQueries() {
      const searchEntry = this.allCitations.find(c => c.type === 'search_queries')
      if (searchEntry && searchEntry.queries && Array.isArray(searchEntry.queries)) {
        return searchEntry.queries
      }
      return []
    },
    // Extract grounded responses with their citation segments mapped to sources
    groundedResponsesWithCitations() {
      if (!this.queries || this.queries.length === 0) {
        return []
      }
      
      const result = []
      
      this.queries.forEach(query => {
        if (!query.citations || !Array.isArray(query.citations)) return
        
        // Get all web sources for this query (to map indices)
        const webSources = query.citations
          .filter(c => c.type === 'web' && c.uri)
          .map(c => ({
            uri: c.uri,
            title: c.title || '',
            final_url: c.final_url || '',
            validated: !!c.validated
          }))
        
        // Get grounding support entries (these link text to source indices)
        const groundingSupports = query.citations.filter(c => c.type === 'grounding_support')
        
        if (groundingSupports.length === 0 || webSources.length === 0) return
        
        // Map each grounding support to its text and sources
        const segments = groundingSupports
          .filter(gs => gs.segment && gs.segment.text && gs.grounding_chunk_indices)
          .map(gs => {
            // Map chunk indices to actual web sources
            const sources = (gs.grounding_chunk_indices || [])
              .filter(idx => idx >= 0 && idx < webSources.length)
              .map(idx => webSources[idx])
              // Deduplicate by URI
              .filter((source, i, arr) => arr.findIndex(s => s.uri === source.uri) === i)
            
            return {
              text: gs.segment.text,
              startIndex: gs.segment.startIndex,
              endIndex: gs.segment.endIndex,
              sources: sources
            }
          })
          .filter(seg => seg.sources.length > 0)
        
        if (segments.length > 0) {
          result.push({
            query_id: query.id,
            query_text: query.query_text,
            segments: segments
          })
        }
      })
      
      return result
    },
    // Total count of grounded segments across all queries
    totalGroundedSegments() {
      return this.groundedResponsesWithCitations.reduce(
        (sum, q) => sum + q.segments.length, 
        0
      )
    },
    /** Mirrors ActionCenterTab `allFindings` length (aggregate diagnosis recommendations). */
    diagnosisActionsCount() {
      const fromApi = this.analytics?.diagnosis_findings
      if (Array.isArray(fromApi) && fromApi.length > 0) return fromApi.length

      let total = 0
      for (const query of this.queries || []) {
        const report = query?.diagnosis_report
        if (!report || !this.isDiagnosisReportDone(report.status)) continue
        total += this.normalizeDiagnosisReportFindings(report.findings).length
      }
      return total
    }
  },
  watch: {
    entityMenuOpen(val) {
      if (val) this.updateEntityPickerMenuWidth()
    },
    selectedEntityId() {
      this.providerList = []
      this.providerComparisonPending = false
      this.refreshProviderList()
      this.updateEntityPickerMenuWidth()
      this.showScanAnimation = false
      this.analysisReady = false
      this.scanEnrichment = null
      // Allow a fresh visit to be recorded for the newly selected entity.
      this.visitRecordedForEntityId = null
      // A different audit gets a fresh backoff and a fresh one-hour budget.
      this.stopSlowRefresh()
      if (this.needsSlowRefresh) this.startSlowRefresh()
    },
    loading(val) {
      if (!val) this.$nextTick(() => this.updateEntityPickerMenuWidth())
    },
    isPipelineBlocking(blocking, wasBlocking) {
      // Prefetch enrichment while the user waits through the pipeline.
      if (blocking && !this.scanEnrichment) {
        this.loadScanEnrichment()
      }
      // User watched steps finish here — show Continue only if the scan can still play.
      // (Refresh also flips null→ready through this watcher; don't show Continue for
      // older entities or ones already marked played in localStorage.)
      if (wasBlocking === true && blocking === false && this.canViewResults) {
        if (shouldPlayScanAnimation(this.selectedEntityId, this.selectedEntityCreatedAt)) {
          this.analysisReady = true
        }
      }
    },
    needsSlowRefresh(needed) {
      if (needed) this.startSlowRefresh()
      else this.stopSlowRefresh()
    },
    // The flag arrives with the user, which can load after this page.
    providerTab(tab, old) {
      if (tab === 'providers') this.refreshProviderList()
      if (old && this.currentTab === old) this.currentTab = tab || 'queries'
    },
    currentTab(val) {
      // The Providers tab pushes its own updates; the others read this copy, so refresh it on switch.
      if (val !== 'providers') this.refreshProviderList()
      // Opening the Visibility dashboard resets the idle clock.
      if (val === 'visibility' && this.selectedEntityId) {
        this.recordVisit(this.selectedEntityId)
      }
    },
    '$route.query.finding_id'(id) {
      if (id) this.currentTab = 'action_center'
    },
  },
  methods: {
    scanEntityPayload() {
      const fromAnalytics = this.analytics?.review_entity
      const fromList = this.entities.find((e) => e.id === this.selectedEntityId)
      return {
        brand_name: fromAnalytics?.brand_name || fromList?.brand_name || '',
        website_url: fromAnalytics?.url || fromList?.url || '',
        selected_service: fromAnalytics?.product_name || fromList?.product_name || '',
        region: fromAnalytics?.region || fromList?.region || '',
        competitors: this.scanCompetitorNames
      }
    },
    async loadScanEnrichment() {
      const payload = this.scanEntityPayload()
      if (!payload.brand_name || !payload.website_url) return
      try {
        const result = await this.$store.dispatch('fetchScanEnrichment', payload)
        if (result?.success) {
          this.scanEnrichment = result
        }
      } catch (error) {
        console.warn('Scan enrichment unavailable:', error)
      }
    },
    beginScanAnimation() {
      if (this.showScanAnimation) return
      if (!shouldPlayScanAnimation(this.selectedEntityId, this.selectedEntityCreatedAt)) return false
      // Mark on start so a mid-animation refresh doesn't loop the scan.
      markScanAnimationPlayed(this.selectedEntityId)
      this.analysisReady = false
      // Show immediately so analytics never flash underneath; animation owns enrichment wait.
      this.showScanAnimation = true
      if (!this.scanEnrichment) {
        this.loadScanEnrichment()
      }
      return true
    },
    finishScanAnimation() {
      this.showScanAnimation = false
      this.analysisReady = false
    },
    /**
     * Admin-only: clear the played record and bump created_at to now (server-side)
     * so the entity re-enters the 24h eligibility window, then reload so Continue
     * re-triggers the scan animation.
     */
    async resetScanAnimationForAdmin() {
      if (!this.selectedEntityId) return
      try {
        await axios.post(`${BASE_URL}/review_entities/${this.selectedEntityId}/reset_scan_animation`, null, {
          headers: { Authorization: localStorage.getItem('auth_token') }
        })
      } catch (error) {
        console.warn('Failed to reset scan animation eligibility:', error)
        return
      }
      clearScanAnimationPlayed(this.selectedEntityId)
      window.location.reload()
    },
    /** Continue CTA after steps complete on this page (or landing from Dashboard). */
    onAnalysisContinue() {
      this.analysisReady = false
      if (!this.beginScanAnimation()) {
        // Ineligible for scan (already played / older than 24h) — fall through to results.
      }
    },
    maybeStartScanForReadyEntity() {
      if (!this.canViewResults || !this.selectedEntityId) return
      if (this.selectedHistoricalDate) return
      if (this.showScanAnimation || this.analysisReady) return

      // Dashboard Continue passes auto_scan=1 so we don't ask twice.
      const autoScan = this.$route?.query?.auto_scan === '1'
      if (autoScan) {
        this.clearAutoScanQuery()
        if (!this.beginScanAnimation()) {
          // Nothing to play — show results.
        }
        return
      }

      // Already on Analytics (or refresh): gate behind Continue when the scan can still play.
      if (shouldPlayScanAnimation(this.selectedEntityId, this.selectedEntityCreatedAt)) {
        this.analysisReady = true
      }
    },
    clearAutoScanQuery() {
      if (this.$route?.query?.auto_scan !== '1') return
      const query = { ...this.$route.query }
      delete query.auto_scan
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    openActionCenterTab() {
      if (this.$route.query.finding_id) {
        const query = { ...this.$route.query }
        delete query.finding_id
        delete query.step
        this.$router.push({ path: '/analytics', query }).catch(() => {})
        this.currentTab = 'action_center'
        return
      }
      this.currentTab = 'action_center'
      this.$nextTick(() => {
        document.querySelector('.tabs-container')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    // Color + label for the idle lifecycle status chip.
    auditStatusChip(status) {
      switch (status) {
        case 'warning':
          return { color: 'warning', text: 'Warning' }
        case 'paused':
          return { color: 'grey', text: 'Paused' }
        case 'archived':
          return { color: 'grey', text: 'Archived' }
        default:
          return { color: 'success', text: 'Active' }
      }
    },
    // "Visit to keep active" — reuses tab switching; the currentTab watcher
    // records the visit and resets the idle clock.
    onVisitKeepActive() {
      this.currentTab = 'visibility'
      this.$nextTick(() => {
        document.querySelector('.tabs-container')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    async recordVisit(entityId) {
      if (!entityId) return
      // Guard against re-recording while staying on the tab for the same entity.
      if (this.visitRecordedForEntityId === entityId) return
      this.visitRecordedForEntityId = entityId
      try {
        const res = await this.$store.dispatch('recordEntityVisit', entityId)
        this.applyEntityStatus(entityId, res?.status)
        // Refresh the list so the picker/banner reflect the reset clock.
        await this.$store.dispatch('fetchReviewEntities')
      } catch (error) {
        // Allow a retry on the next tab entry if it failed.
        this.visitRecordedForEntityId = null
        console.error('Failed to record entity visit:', error)
      }
    },
    async onPauseAudit() {
      const entityId = this.selectedEntityId
      if (!entityId || this.auditActionLoading) return
      this.auditActionLoading = true
      try {
        const res = await this.$store.dispatch('pauseReviewEntity', entityId)
        this.applyEntityStatus(entityId, res?.status, 'manual')
        await this.$store.dispatch('fetchReviewEntities')
      } catch (error) {
        console.error('Failed to pause audit:', error)
      } finally {
        this.auditActionLoading = false
      }
    },
    async onUnpauseAudit() {
      const entityId = this.selectedEntityId
      if (!entityId || this.auditActionLoading) return
      this.auditActionLoading = true
      try {
        const res = await this.$store.dispatch('unpauseReviewEntity', entityId)
        // Unpausing also resets the clock, so treat it as freshly visited.
        this.visitRecordedForEntityId = entityId
        this.applyEntityStatus(entityId, res?.status, null)
        await this.$store.dispatch('fetchReviewEntities')
      } catch (error) {
        console.error('Failed to unpause audit:', error)
      } finally {
        this.auditActionLoading = false
      }
    },
    // Pause a specific entity from the picker dropdown (reuses the pause flow by
    // row id and keeps the menu open). No-op if already paused.
    async pauseEntityFromMenu(item) {
      if (!item || item.status === 'paused' || this.auditActionLoading) return
      this.auditActionLoading = true
      try {
        const res = await this.$store.dispatch('pauseReviewEntity', item.id)
        // Updates the banner only when this row is the selected entity.
        this.applyEntityStatus(item.id, res?.status, 'manual')
        // Refresh the list so the row's status chip reflects the pause.
        await this.$store.dispatch('fetchReviewEntities')
      } catch (error) {
        console.error('Failed to pause entity:', error)
      } finally {
        this.auditActionLoading = false
      }
    },
    // Optimistically reflect a status change on the loaded detail payload so the
    // banner updates immediately, before the list refetch resolves.
    applyEntityStatus(entityId, status, pauseReason) {
      const detail = this.analytics?.review_entity
      if (!detail || Number(detail.id) !== Number(entityId)) return
      if (status) detail.status = status
      if (pauseReason !== undefined) detail.pause_reason = pauseReason
    },
    // Handle email deep-links (?action=pause / ?tab=visibility) after the
    // selected entity and its analytics have loaded.
    async handleDeepLinkActions() {
      const action = this.$route?.query?.action
      const tab = this.$route?.query?.tab
      const findingId = this.$route?.query?.finding_id
      if (!action && !tab && !findingId) return

      if (action === 'pause') {
        await this.onPauseAudit()
      } else if (tab === 'visibility') {
        this.currentTab = 'visibility' // the currentTab watcher records the visit
      } else if (tab === 'action_center' || findingId) {
        this.currentTab = 'action_center'
      }

      // Strip one-shot params so a refresh doesn't re-trigger the action.
      const query = { ...this.$route.query }
      delete query.action
      delete query.tab
      this.$router.replace({ query }).catch(() => {})
    },
    onBrandMatchAliasesUpdated(aliases) {
      if (!this.analytics?.review_entity) return
      this.analytics.review_entity.brand_match_aliases = aliases
    },
    async refreshProviderList() {
      const id = this.selectedEntityId
      if (!id || !this.providersPageEnabled) return
      try {
        const res = await listProviders(id)
        if (id === this.selectedEntityId) this.providerList = res.providers || []
      } catch (e) {
        // keep the last list; the Providers tab shows its own load error
      }
    },
    onGoToTab(tab) {
      // Links to the Providers tab land on the old Provider comparison tab without the flag.
      this.currentTab = tab === 'providers' ? (this.providerTab || 'queries') : tab
      this.$nextTick(() => {
        document.querySelector('.tabs-container')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    // The server's diagnosis setup counts providers and holds Run Diagnosis while
    // one is pending, so refetch it (keeping any historical date) after a change.
    onProvidersChanged() {
      if (!this.selectedEntityId) return
      return this.fetchAnalytics(this.selectedEntityId, false, { asOfDate: this.selectedHistoricalDate || null })
    },
    onProviderNamesChanged(count) {
      const assessmentId = this.analytics?.diagnosis_setup?.assessment_id
      if (!assessmentId) return
      // Providers can't be removed, so never lower the count: a failed provider
      // fetch briefly empties the tab's list.
      const current = this.providerCountsByAssessment[assessmentId] || 0
      this.providerCountsByAssessment = {
        ...this.providerCountsByAssessment,
        [assessmentId]: Math.max(current, count)
      }
    },
    onDiagnosisSetupUpdated(setup) {
      if (!this.analytics || !setup) return
      this.analytics.diagnosis_setup = setup
    },
    async onDiagnosisStarted(setup) {
      this.onDiagnosisSetupUpdated(setup)
      if (!this.selectedEntityId) return
      if (this.selectedHistoricalDate) {
        this.selectedHistoricalDate = null
        this.syncHistoricalDateQuery(null)
      }
      await this.fetchAnalytics(this.selectedEntityId, false)
    },
    async onVisibilityRescored() {
      if (!this.selectedEntityId) return
      if (this.selectedHistoricalDate) {
        this.selectedHistoricalDate = null
        this.syncHistoricalDateQuery(null)
      }
      await this.fetchAnalytics(this.selectedEntityId, false)
    },
    isDiagnosisReportDone(status) {
      return String(status || '').trim().toLowerCase() === 'done'
    },
    isDiagnosisReportPending(status) {
      return ['pending', 'processing', 'dispatching'].includes(String(status || '').trim().toLowerCase())
    },
    normalizeDiagnosisReportFindings(rawFindings) {
      if (!rawFindings) return []

      let value = rawFindings
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value)
        } catch {
          return []
        }
      }

      if (Array.isArray(value)) return value

      if (value && typeof value === 'object') {
        if (Array.isArray(value.findings)) return value.findings
        const objectValues = Object.values(value)
        if (objectValues.every((item) => item && typeof item === 'object')) {
          return objectValues
        }
      }

      return []
    },
    updateEntityPickerMenuWidth() {
      this.$nextTick(() => {
        const ref = this.$refs.entityPickerActivatorRef
        const el = ref && (ref.$el || ref)
        if (el && typeof el.offsetWidth === 'number' && el.offsetWidth > 0) {
          this.entityPickerMenuWidth = el.offsetWidth
        }
      })
    },
    isCurrentUserCreator(entity) {
      if (!entity) return false
      if (!this.currentUserId) return false
      return entity.creator_id === this.currentUserId
    },
    isAssessmentProcessing(entity) {
      if (!entity || !entity.latest_assessment) return false
      const status = entity.latest_assessment.status
      return status === 'pending' || status === 'processing'
    },
    // An unfinished scan paused on a step the user has to confirm.
    isAwaitingUserInput(entity) {
      return this.isAssessmentProcessing(entity) &&
        AWAITING_USER_STAGES.includes(entity.latest_assessment.processing_stage)
    },
    // An unfinished scan with jobs actually running.
    isAssessmentScanning(entity) {
      return this.isAssessmentProcessing(entity) && !this.isAwaitingUserInput(entity)
    },
    startPolling() {
      // Clear any existing interval
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
      
      // Only poll if the currently selected entity is processing.
      // This avoids repeatedly refetching the whole sidebar list.
      if (this.selectedEntityId && this.assessmentNeedsPolling) {
        if (!this.selectedHistoricalDate) {
          this.fetchAnalytics(this.selectedEntityId, false).catch(() => {})
        }
        
        // Then continue polling every 3 seconds
        this.pollingInterval = setInterval(async () => {
          // Stop if the selected entity no longer needs polling.
          if (!this.selectedEntityId || !this.assessmentNeedsPolling) {
            this.stopPolling()
            return
          }

          // Don't poll analytics while viewing or loading a historical date snapshot.
          if (this.selectedHistoricalDate || this.loadingSnapshot) return

          // Prevent overlapping requests.
          if (this.pollingInFlight) return

          this.pollingInFlight = true
          try {
            await this.fetchAnalytics(this.selectedEntityId, false)
          } finally {
            this.pollingInFlight = false
          }

          // Re-check after refresh.
          if (!this.assessmentNeedsPolling) {
            this.stopPolling()
          }
        }, 3000) // Poll every 3 seconds
      }
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },
    startSlowRefresh() {
      if (this.slowRefreshTimer) return
      this.slowRefreshStartedAt = Date.now()
      this.slowRefreshTick = 0
      this.scheduleSlowRefresh()
    },
    scheduleSlowRefresh() {
      // A refresh still in flight when the page unmounts finishes afterwards; don't
      // let it restart the chain (beforeUnmount can only clear a pending timer).
      if (this._unmounted) return
      const delays = SLOW_REFRESH_DELAYS_MS
      const delay = delays[Math.min(this.slowRefreshTick, delays.length - 1)]
      this.slowRefreshTimer = setTimeout(() => this.runSlowRefresh(), delay)
    },
    async runSlowRefresh() {
      this.slowRefreshTimer = null
      if (!this.selectedEntityId || !this.needsSlowRefresh) return
      if (Date.now() - this.slowRefreshStartedAt > SLOW_REFRESH_MAX_MS) return

      this.slowRefreshTick += 1
      // Skip this tick (but keep the chain) while the tab is hidden, another fetch
      // owns the screen, the 3s pipeline poll is running, or a snapshot is open.
      const busy = this.loading || this.slowRefreshInFlight || this.pollingInterval ||
        this.selectedHistoricalDate || this.loadingSnapshot
      if (!document.hidden && !busy) {
        this.slowRefreshInFlight = true
        try {
          await this.fetchAnalytics(this.selectedEntityId, false)
        } finally {
          this.slowRefreshInFlight = false
        }
      }
      if (this.needsSlowRefresh && !this.slowRefreshTimer) this.scheduleSlowRefresh()
    },
    stopSlowRefresh() {
      if (this.slowRefreshTimer) {
        clearTimeout(this.slowRefreshTimer)
        this.slowRefreshTimer = null
      }
    },
    async fetchEntities(isPolling = false) {
      try {
        // Only fetch from API if polling (to get updated status)
        // Otherwise, use entities already in store from App.vue
        if (isPolling) {
          await this.$store.dispatch('fetchReviewEntities')
        }
        
        const wasProcessing = this.assessmentNeedsPolling
        
        if (this.entities.length > 0) {
          const fromQuery = this.$route?.query?.review_entity_id && Number(this.$route.query.review_entity_id)
          const exists = fromQuery && this.entities.some(e => e.id === fromQuery)
          // Only apply route query on initial load - during polling preserve user's entity selection.
          // Previously, every poll overwrote selectedEntityId with the URL param, making manual selection appear to fail.
          if (!isPolling) {
            this.selectedEntityId = exists ? fromQuery : (this.selectedEntityId || this.entities[0].id)
          } else {
            // During polling: ensure selection is valid (entity still exists) but don't overwrite from route
            const selectionValid = this.selectedEntityId && this.entities.some(e => e.id === this.selectedEntityId)
            if (!selectionValid) {
              this.selectedEntityId = this.entities[0].id
            }
          }
          
          // Check if current entity is in a confirmation stage - stop polling if so
          const currentEntity = this.entities.find(e => e.id === this.selectedEntityId)
          const isInConfirmationStage = needsCompetitorConfirmation(currentEntity?.latest_assessment) ||
                                       currentEntity?.latest_assessment?.processing_stage === 'confirm_personas' ||
                                       ['confirm_queries', 'confirm_provider_queries'].includes(currentEntity?.latest_assessment?.processing_stage)
          
          if (isInConfirmationStage) {
            // Stop polling during confirmation stages
            this.stopPolling()
            // Still fetch analytics to show the confirmation UI
            if (!isPolling) {
              await this.fetchAnalytics(this.selectedEntityId, true)
            } else {
              await this.fetchAnalytics(this.selectedEntityId, false)
            }
          } else {
            // If we're polling and current entity status changed from processing to complete, fetch full analytics
            if (isPolling && wasProcessing && !this.assessmentNeedsPolling) {
              await this.fetchAnalytics(this.selectedEntityId, false) // Don't show loading during poll
            } else if (!isPolling) {
              // fetchAnalytics will manage its own loading state
              await this.fetchAnalytics(this.selectedEntityId, true)
            }
            
            // Only manage polling if NOT already polling (to avoid recursive interval creation)
            // When polling, the interval callback will check and stop itself if needed
            if (!isPolling) {
              if (this.hasAnyProcessingEntities) {
                this.startPolling()
              } else {
                this.stopPolling()
              }
            } else {
              // If we're already polling, just stop if no entities are processing
              // The interval callback will also check, but this is a safety check
              if (!this.hasAnyProcessingEntities) {
                this.stopPolling()
              }
            }
          }
        } else {
          // No entities found, show empty state
          this.loading = false
        }
      } catch (error) {
        console.error('Error fetching entities:', error)
        this.loading = false
      }
    },
    async fetchAnalytics(entityId, showLoading = true, options = {}) {
      const { snapshotTransition = false, asOfDate = null } = options
      const fetchId = ++this.analyticsFetchId

      // Never swap the whole dashboard for a historical date fetch.
      if (snapshotTransition) {
        this.loading = false
        this.loadingSnapshot = true
        this.snapshotLoadTarget = asOfDate
        this.snapshotError = null
      } else if (showLoading) {
        this.loading = true
      }
      try {
        // Use selectedProvider if set, otherwise use the assessment's default provider
        const provider = this.selectedProvider || this.currentProvider
        const params = provider ? { llm_provider: provider } : {}
        if (asOfDate) {
          params.as_of_date = asOfDate
        }

        const response = await axios.get(`${BASE_URL}/review_entities/${entityId}`, {
          params: params,
          headers: { Authorization: localStorage.getItem('auth_token') }
        })

        // Ignore stale responses when a newer fetch was started.
        if (fetchId !== this.analyticsFetchId) return

        this.analytics = response.data.data
        
        // Initialize selectedProvider from assessment if not already set
        if (!this.selectedProvider && this.analytics?.latest_assessment?.llm_provider) {
          this.selectedProvider = this.analytics.latest_assessment.llm_provider
        }

        if (!asOfDate) {
          this.maybeStartScanForReadyEntity()
        }
      } catch (error) {
        if (fetchId !== this.analyticsFetchId) return
        console.error('Error fetching analytics:', error)
        if (snapshotTransition) {
          this.snapshotError = asOfDate
            ? 'Failed to load scores for that date. Try again or choose another date.'
            : "Failed to load today's scores. Try again."
        }
      } finally {
        if (fetchId !== this.analyticsFetchId) return
        // The latest fetch owns the loading flag: a silent refresh can supersede a
        // loud one, and the loud one's finally returns above without clearing it.
        if (!snapshotTransition) {
          this.loading = false
        }
        if (snapshotTransition) {
          this.loading = false
          this.loadingSnapshot = false
          this.snapshotLoadTarget = null
        }
      }
    },
    async onProviderChange() {
      // When provider changes, refetch analytics with the new provider
      if (this.selectedEntityId) {
        if (this.selectedHistoricalDate) {
          await this.fetchAnalytics(this.selectedEntityId, false, {
            snapshotTransition: true,
            asOfDate: this.selectedHistoricalDate
          })
        } else {
          await this.fetchAnalytics(this.selectedEntityId, true)
        }
      }
    },
    // Sources tab empty state ("switch to Gemini Grounded") drives the provider
    // toggle from inside the tab.
    async onSwitchProvider(provider) {
      if (!provider || this.selectedProvider === provider) return
      this.selectedProvider = provider
      await this.onProviderChange()
    },
    async onHistoricalDateSelected(date) {
      if (!this.selectedEntityId || date === this.selectedHistoricalDate) return
      if (!isWithinHistoricalWindow(date)) {
        this.snapshotError = `Historical data is only available for the last ${HISTORICAL_WINDOW_DAYS} days.`
        return
      }
      this.selectedHistoricalDate = date
      this.syncHistoricalDateQuery(date)
      await this.fetchAnalytics(this.selectedEntityId, false, {
        snapshotTransition: true,
        asOfDate: date
      })
    },
    async onHistoricalDateCleared() {
      if (!this.selectedHistoricalDate && !this.loadingSnapshot) return
      this.selectedHistoricalDate = null
      this.syncHistoricalDateQuery(null)
      if (this.selectedEntityId) {
        await this.fetchAnalytics(this.selectedEntityId, false, {
          snapshotTransition: true,
          asOfDate: null
        })
      }
    },
    syncHistoricalDateQuery(date) {
      const query = { ...this.$route.query }
      if (date) {
        query.as_of_date = date
      } else {
        delete query.as_of_date
      }
      this.$router.replace({ query }).catch(() => {})
    },
    async onSelectEntity(id) {
      this.selectedEntityId = id
      this.selectedHistoricalDate = null
      this.snapshotError = null
      const query = { ...this.$route.query, review_entity_id: id }
      delete query.as_of_date
      delete query.finding_id
      delete query.step
      this.$router.replace({ path: '/analytics', query }).catch(() => {})
      this.selectedProvider = 'google_grounded'
      await this.fetchAnalytics(id, true) // Show loading when switching entities

      // Switching entities while on the Visibility tab counts as a visit
      // (the currentTab watcher won't fire since the tab didn't change).
      if (this.currentTab === 'visibility') {
        this.recordVisit(id)
      }

      // Start or stop polling based on whether any entity is processing
      if (this.hasAnyProcessingEntities) {
        this.startPolling()
      } else {
        this.stopPolling()
      }
    },
    async selectEntityFromMenu(id) {
      this.entityMenuOpen = false
      if (id === this.selectedEntityId) return
      await this.onSelectEntity(id)
    },
    viewCurrentEntity() {
      // This method is called when user clicks "View Details" on the processing component
      // Since we're already on the analytics page, we don't need to navigate anywhere
      // The user can see the processing status and wait for completion
      // Or they can select a different entity from the header menu
    },
    async handleCompetitorsConfirmed() {
      // Refresh assessment data immediately after confirmation
      // The backend updates the stage immediately when confirming
      await this.$store.dispatch('fetchReviewEntities')
      await this.fetchAnalytics(this.selectedEntityId, false)
      
      // Resume polling for the next processing stage (e.g. competitor comparison)
      const currentEntity = this.entities.find(e => e.id === this.selectedEntityId)
          const isInConfirmationStage = needsCompetitorConfirmation(currentEntity?.latest_assessment) ||
                                       currentEntity?.latest_assessment?.processing_stage === 'confirm_personas' ||
                                       ['confirm_queries', 'confirm_provider_queries'].includes(currentEntity?.latest_assessment?.processing_stage)
      
      if (!isInConfirmationStage && this.hasAnyProcessingEntities) {
        this.startPolling()
      }
    },
    async handlePersonasConfirmed() {
      // Refresh assessment data immediately after confirmation
      // The backend updates the stage immediately when confirming
      await this.$store.dispatch('fetchReviewEntities')
      await this.fetchAnalytics(this.selectedEntityId, false)
      
      // Resume polling since we're now in queries_generation stage
      const currentEntity = this.entities.find(e => e.id === this.selectedEntityId)
          const isInConfirmationStage = needsCompetitorConfirmation(currentEntity?.latest_assessment) ||
                                       currentEntity?.latest_assessment?.processing_stage === 'confirm_personas' ||
                                       ['confirm_queries', 'confirm_provider_queries'].includes(currentEntity?.latest_assessment?.processing_stage)
      
      if (!isInConfirmationStage && this.hasAnyProcessingEntities) {
        this.startPolling()
      }
    },
    async handleQueriesConfirmed() {
      // Refresh assessment data immediately after confirmation
      // The backend updates the stage immediately when confirming
      await this.$store.dispatch('fetchReviewEntities')
      await this.fetchAnalytics(this.selectedEntityId, false)
      
      // Resume polling since we're now in query_execution stage
      const currentEntity = this.entities.find(e => e.id === this.selectedEntityId)
          const isInConfirmationStage = needsCompetitorConfirmation(currentEntity?.latest_assessment) ||
                                       currentEntity?.latest_assessment?.processing_stage === 'confirm_personas' ||
                                       ['confirm_queries', 'confirm_provider_queries'].includes(currentEntity?.latest_assessment?.processing_stage)
      
      if (!isInConfirmationStage && this.hasAnyProcessingEntities) {
        this.startPolling()
      }
    },
    async handleEditPersonas() {
      // Refresh assessment data to show persona confirmation UI
      await this.$store.dispatch('fetchReviewEntities')
      await this.fetchAnalytics(this.selectedEntityId, false)
    },
    extractDomain(url) {
      try {
        const urlObj = new URL(url)
        return urlObj.hostname.replace('www.', '')
      } catch {
        return url
      }
    },
    confirmDeleteEntity(entity) {
      this.entityToDelete = entity
      this.deleteDialog = true
    },
    async deleteEntity() {
      if (!this.entityToDelete) return
      
      this.deleteLoading = true
      try {
        await axios.delete(`${BASE_URL}/review_entities/${this.entityToDelete.id}`, {
          headers: { Authorization: localStorage.getItem('auth_token') }
        })
        
        // Close dialog
        this.deleteDialog = false
        
        // If we deleted the currently selected entity, reset selection
        const wasSelected = this.entityToDelete.id === this.selectedEntityId
        
        // Refresh entities from store
        await this.$store.dispatch('fetchReviewEntities')
        
        // If deleted entity was selected, select the first available entity
        if (wasSelected && this.entities.length > 0) {
          this.selectedEntityId = this.entities[0].id
          await this.fetchAnalytics(this.selectedEntityId, true)
        } else if (this.entities.length === 0) {
          // No entities left
          this.selectedEntityId = null
          this.analytics = null
        }
        
        this.entityToDelete = null
      } catch (error) {
        console.error('Error deleting entity:', error)
        // Optionally show error snackbar here
      } finally {
        this.deleteLoading = false
      }
    },
    async duplicateEntity() {
      if (!this.entityToDelete) return
      
      this.duplicateLoading = true
      try {
        const response = await axios.post(
          `${BASE_URL}/review_entities/${this.entityToDelete.id}/duplicate`,
          {},
          {
            headers: { Authorization: localStorage.getItem('auth_token') }
          }
        )
        
        // Close dialog
        this.deleteDialog = false
        this.entityToDelete = null
        
        // Refresh entities from store
        await this.$store.dispatch('fetchReviewEntities')
        
        // Select the newly created entity
        if (response.data.success && response.data.review_entity?.id) {
          this.selectedEntityId = response.data.review_entity.id
          await this.fetchAnalytics(this.selectedEntityId, true)
          
          // Start polling if the new entity is processing
          if (this.hasAnyProcessingEntities) {
            this.startPolling()
          }
        }
      } catch (error) {
        console.error('Error duplicating entity:', error)
        // Optionally show error snackbar here
      } finally {
        this.duplicateLoading = false
      }
    }
  },
  async mounted() {
    this._onEntityPickerResize = () => this.updateEntityPickerMenuWidth()
    window.addEventListener('resize', this._onEntityPickerResize)

    // Wait for Vue reactivity to settle
    await this.$nextTick()
    
    // Fetch entities immediately if not in store
    if (this.entities.length === 0) {
      await this.$store.dispatch('fetchReviewEntities')
    }
    
    // Check if entities exist in store
    if (this.entities.length > 0) {
      // Entities available, fetch analytics for selected/first entity immediately
      const fromQuery = this.$route?.query?.review_entity_id && Number(this.$route.query.review_entity_id)
      const exists = fromQuery && this.entities.some(e => Number(e.id) === fromQuery)
      this.selectedEntityId = exists ? fromQuery : this.entities[0].id

      const providerFromQuery = this.$route?.query?.llm_provider
      if (typeof providerFromQuery === 'string' && this.providerOptions.some((opt) => opt.value === providerFromQuery)) {
        this.selectedProvider = providerFromQuery
      }

      if (this.$route?.query?.finding_id) {
        this.currentTab = 'action_center'
      }

      const urlDate = this.$route?.query?.as_of_date
      if (typeof urlDate === 'string' && isWithinHistoricalWindow(urlDate)) {
        this.selectedHistoricalDate = urlDate
        await this.fetchAnalytics(this.selectedEntityId, false, {
          snapshotTransition: true,
          asOfDate: urlDate
        })
      } else {
        if (typeof urlDate === 'string') {
          this.syncHistoricalDateQuery(null)
        }
        await this.fetchAnalytics(this.selectedEntityId, true)
      }

      // Handle email deep-links (?action=pause / ?tab=visibility) once loaded.
      await this.handleDeepLinkActions()

      // Start polling immediately if any entities are processing
      if (this.hasAnyProcessingEntities) {
        this.startPolling()
      }
    } else {
      // No entities found - show empty state
      this.loading = false
    }

    await this.$nextTick()
    this.updateEntityPickerMenuWidth()
  },
  beforeUnmount() {
    this._unmounted = true
    this.stopPolling()
    this.stopSlowRefresh()
    if (this._onEntityPickerResize) {
      window.removeEventListener('resize', this._onEntityPickerResize)
    }
  }
}
</script>

<style scoped>
.v-card {
  border-radius: 16px;
}

.text-h3 {
  font-size: 2.5rem !important;
}

/* Tabs Container */
.tabs-container {
  background: #fff;
  border-radius: 16px;
  border: 1px solid var(--brand-sand-300);
  box-shadow: 0 2px 8px rgba(13, 30, 53, 0.06);
  padding: 8px;
}

/* Persona Sections */
.persona-section {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid var(--brand-sand-300);
  box-shadow: 0 2px 8px rgba(13, 30, 53, 0.06);
  transition: transform 0.2s, box-shadow 0.2s;
  height: 100%;
}

.persona-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.persona-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid var(--brand-sand-100);
}

.persona-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--brand-ink-950);
}

.persona-content {
  padding: 4px 0;
}

.persona-item {
  display: flex;
  align-items: flex-start;
  padding: 16px 0;
  border-bottom: 1px solid #f5f5f5;
}

.persona-item:last-child {
  border-bottom: none;
}

/* Persona Cards */
.persona-card {
  border-radius: 16px;
  border: 1px solid var(--brand-sand-300);
  transition: transform 0.2s, box-shadow 0.2s;
}

.persona-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.persona-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--brand-ink-950);
}

/* Persona Query Header */
.persona-query-card {
  border-radius: 20px;
  border: 1px solid var(--brand-ocean-100);
  background: #f0f8fd;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.persona-query-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--brand-ink-900);
}

.persona-query-motivation {
  font-size: 0.95rem;
  color: var(--brand-ink-800);
  max-width: 580px;
}

.persona-query-details {
  font-size: 0.8rem;
  color: #5f6368;
}

.persona-query-detail-item {
  display: inline-flex;
  align-items: center;
}

.persona-detail-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
}

.persona-detail-icon {
  margin-right: 8px;
  margin-top: 2px;
  color: #5f6368;
}

.persona-detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: none;
  color: #5f6368;
}

.persona-detail-value {
  font-size: 0.9rem;
  color: #202124;
}

.persona-query-product {
  color: var(--brand-ocean-600);
}

.persona-query-chip {
  font-weight: 600;
  letter-spacing: 0.02em;
}

.persona-query-panel {
  border-radius: 18px;
  border: 1px solid #e3e8f5;
  overflow: hidden;
}

.persona-query-panel .v-expansion-panel-title {
  padding: 18px 24px;
}

.persona-query-panel .v-expansion-panel-text {
  padding: 20px 28px 28px;
}

/* Formatted Response – :deep() needed for v-html content */
.formatted-response {
  line-height: 1.6;
}

.formatted-response :deep(ul),
.formatted-response :deep(ol) {
  padding-left: 2rem;
}

.formatted-response :deep(li) {
  padding-left: 0.5rem;
}

/* Grounded Response Analysis Section */
.grounded-panels {
  margin-bottom: 0;
}

.grounded-query-text {
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
  white-space: normal;
  line-height: 1.5;
}

.grounded-segment {
  border: 1px solid var(--brand-sand-300);
  border-radius: 12px;
  overflow: hidden;
}

.segment-text {
  background: linear-gradient(135deg, #fdfcf8 0%, #f7f4ec 100%);
  border-bottom: 1px solid var(--brand-sand-300);
  font-size: 14px;
  line-height: 1.6;
  color: var(--brand-ink-900);
  display: flex;
  align-items: flex-start;
}

.segment-quote-icon {
  color: var(--brand-teal-500);
  margin-top: 2px;
  flex-shrink: 0;
}

.segment-content {
  flex: 1;
}

.segment-sources {
  background-color: var(--brand-sand-50);
}

.sources-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.source-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  gap: 4px;
  background-color: #fff;
  border: 1px solid var(--brand-sand-300);
  border-radius: 16px;
  font-size: 12px;
  color: var(--brand-ocean-600);
  text-decoration: none;
  transition: all 0.2s;
  max-width: 220px;
  min-width: 0;
}

.source-chip-icon {
  flex-shrink: 0;
}

.source-chip-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.source-chip-check {
  flex-shrink: 0;
  margin-left: 2px;
}

.source-chip:hover {
  background-color: var(--brand-ocean-100);
  border-color: var(--brand-ocean-600);
  box-shadow: 0 2px 4px rgba(33, 105, 168, 0.15);
}

.search-queries-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.team-creator-text {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-style: italic;
}

/* Same wording and colour as the Dashboard's "Pending" chip (scan waiting on the user). */
.entity-pending-text {
  color: #8b5f16;
}

.team-subheader {
  padding-left: 24px;
}

.team-entity-item {
  padding-left: 32px;
}

.entity-picker-list {
  max-height: min(55vh, 420px);
  overflow-y: auto;
}

.min-width-0 {
  min-width: 0;
}

.aeo-page-container {
  max-width: 100%;
  padding-inline: 12px;
  padding-block: 12px;
}

@media (min-width: 600px) {
  .aeo-page-container {
    padding-inline: 20px;
    padding-block: 16px;
  }
}

@media (min-width: 1264px) {
  .aeo-page-container {
    padding-inline: 28px;
  }
}

.aeo-page-row {
  margin: 0;
}

.aeo-page-col {
  padding: 0;
  max-width: 100%;
}

.entity-picker-trigger {
  max-width: min(100%, 560px);
  width: 100%;
  padding-inline: 14px 12px !important;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface)) !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.entity-picker-trigger :deep(.v-btn__overlay) {
  opacity: 0 !important;
}

.entity-picker-trigger :deep(.v-btn__content) {
  width: 100%;
  justify-content: flex-start;
  gap: 0;
}

.entity-picker-trigger:hover,
.entity-picker-trigger:focus-visible {
  border-color: rgba(var(--v-theme-primary), 0.32);
  background: rgb(var(--v-theme-surface)) !important;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.05),
    inset 0 0 0 9999px rgba(var(--v-theme-primary), 0.07);
}

.entity-picker-trigger[aria-expanded='true'] {
  border-color: rgba(var(--v-theme-primary), 0.4);
  background: rgb(var(--v-theme-surface)) !important;
  box-shadow:
    0 1px 4px rgba(0, 0, 0, 0.06),
    inset 0 0 0 9999px rgba(var(--v-theme-primary), 0.1);
}

.entity-picker-panel {
  width: 100%;
  max-width: none;
  overflow: hidden;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-sizing: border-box;
}

.entity-picker-list :deep(.v-list-item) {
  min-height: 72px;
  padding-block: 10px;
  padding-inline: 12px 10px;
}

.entity-picker-list :deep(.v-list-item__append) {
  align-self: center;
}

.entity-picker-list :deep(.v-list-item-title) {
  font-weight: 600;
  line-height: 1.35;
  white-space: normal;
  word-break: break-word;
}

.entity-picker-list :deep(.v-list-item-subtitle) {
  margin-top: 4px;
  line-height: 1.4;
  white-space: normal;
  word-break: break-word;
}

.entity-picker-filter-wrap {
  background: rgb(var(--v-theme-surface));
}

.entity-picker-filter :deep(.v-field) {
  box-shadow: none !important;
}

.entity-picker-filter :deep(.v-field--variant-solo-filled) {
  background: rgba(var(--v-theme-on-surface), 0.06) !important;
}

.provider-select-toolbar {
  max-width: 200px;
}

.diagnosis-actions-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  border-radius: 999px;
  background: var(--brand-ink-950);
  border: 1px solid var(--brand-sand-300);
  box-sizing: border-box;
  cursor: pointer;
  font: inherit;
  vertical-align: middle;
  transition: filter 0.15s ease, box-shadow 0.15s ease;
}

.diagnosis-actions-badge:hover {
  filter: brightness(1.08);
}

.diagnosis-actions-badge:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.diagnosis-actions-badge-icon {
  color: var(--brand-amber-400);
}

.diagnosis-actions-badge-count {
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1;
}

.diagnosis-actions-badge-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(218, 238, 250, 0.85);
  letter-spacing: 0.02em;
}

.aeo-action-tab-lightning {
  color: var(--brand-ocean-600) !important;
}
</style>

